import classNames from "classnames";
import clsx from "clsx";
import { GraphQLError } from "graphql";
import isEmpty from "is-empty";
import moment, { isDate } from "moment";
import PropTypes from "prop-types";
import React, {
  ChangeEvent,
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isInclusivelyAfterDay } from "react-dates";
import toast from "react-hot-toast";
import { addProductToOccupantMutation } from "rsl-api-106/lib/mutations";
import { getOccupantProductsQuery } from "rsl-api-106/lib/queries";
import {
  Cart,
  Lineitem,
  Maybe,
  Mutation,
  MutationAddProductToOccupantArgs,
  Occupant,
  Product,
  ProductByCategory,
  ProductDistributionType,
  Query,
  QueryGetOccupantProductsArgs,
} from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import Button from "@components/_Layout/Controls/Button";
import InputText from "@components/_Layout/Controls/InputText";
import SDPicker from "@components/_Layout/Controls/SDPicker";
import Select from "@components/_Layout/Controls/Select";
import { MyAccordion } from "@components/_Layout/Disclosure/Disclosure";
import LoadingSpinner from "@components/_Layout/Loaders/LoadingSpinner";
import SeeMore from "@components/_Layout/SeeMore";
import ProductList from "@components/Products/ProductList";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tab } from "@headlessui/react";
import { useCart, useConfig, useSession } from "@hooks";
import { CheckOutlined, ChevronRightOutlined } from "@mui/icons-material";
import {
  deleteCachedOccupantProducts,
  getCachedProducts,
  setCachedProducts,
} from "@utils/cache";
import { getCurrencySymbol } from "@utils/currencies";
import { compareDates } from "@utils/dates";
import {
  focusElementById,
  getAbsoluteImagePath,
  replaceImgOnError,
} from "@utils/dom";

type OccupantProps = {
  cartItemID: number;
  stayGuiid: number;
  occupant: Occupant;
  index: number;
  showProducts?: boolean;
  showValidity?: boolean;
  canEditOccupant?: boolean;
  showInfoOccupant?: boolean;
  disableEditFirstOccupant?: boolean;
};
type TOccupantInfos = {
  firstName: Maybe<string> | undefined;
  lastName: Maybe<string> | undefined;
  birthDate: Date | string | null | undefined;
};

type ProductOrder = {
  order: number;
  product: Product;
};

const isValidOccupant = (
  firstName: string,
  lastName: string,
  birthDate: Date
): boolean => {
  return !isEmpty(firstName) && !isEmpty(lastName) && !isEmpty(birthDate);
};

const languagesVarsCodes = [
  "LANG_neobooking_occupant", //Occupant
  "LANG_neobooking_occupant_removing_in_progress", //Suppression de l'occupant en cours
  "LANG_neobooking_occupant_successfully_removed", //L'occupant a bien été enlevé !
  "LANG_neobooking_updating_occupant_data_in_progress", //Enregistrement des informations de l'occupant en cours
  "LANG_neobooking_occupant_data_successfully_updated", //Les informations de l'occupant ont bien été mises à jour.
  "LANG_neobooking_occupant_invalid_birthdate", //La date de naissance que vous avez entrée n'est pas valide.
  "LANG_neobooking_lastname", //Nom
  "LANG_neobooking_firstname", //Prénom
  "LANG_neobooking_birthdate", //Date de naissance
  "LANG_neobooking_product_occupant_title", //Options par occupant
  "LANG_neobooking_occupant_remove_this_occupant", //Supprimer cet occupant
  "LANG_neobooking_add", //Ajouter
  "LANG_neobooking_delete", //Supprimer
  "LANG_neobooking_see_more", //+ Voir plus
  "LANG_neobooking_product_occupant_book_forfait", //Réservez votre forfait
  "LANG_neobooking_product_occupant_book_mat", //Réservez votre matériel
  "LANG_neobooking_product_apply_to_occupant", //Appliquer ma sélection a tous les occupants
  "LANG_neobooking_product_copy_to_occupant", //Les prestations ont bien été copiées sur tout les autres occupants !
];
const OccupantItem: React.FC<OccupantProps> = ({
  cartItemID,
  stayGuiid,
  occupant,
  index,
  showProducts,
  showValidity,
  canEditOccupant = true,
  showInfoOccupant = true,
  disableEditFirstOccupant = false,
}) => {
  /* Hooks */
  const { getLanguageVarValue, authMode } = useSession();
  const { cart, removeOccupant, updateOccupant } = useCart();
  useEffect(() => {
    if (cart && (cart?.reservations?.length ?? -1 > 0)) {
      setOccupantInfos({
        firstName: occupant.firstName,
        lastName: occupant.lastName,
        birthDate: occupant.birthDate,
      });
    }
  }, [cart]);
  /* Sumbitting State */
  const [isSubmittingRemoveOccupant, setIsSubmittingRemoveOccupant] =
    useState<boolean>(false);
  const [occupantSaved, setOccupantSaved] = useState<number>(0);
  //occupant info state
  const [occupantInfos, setOccupantInfos] = useState<TOccupantInfos>({
    firstName: occupant.firstName,
    lastName: occupant.lastName,
    birthDate: occupant.birthDate,
  });

  const isFromFCI = cart?.fromFastCheckinCheckout;

  //Ne permettre la suppression des occupants que si et seulement si l'occupant n'a pas de prestation à la personne non obligatoires.
  const canRemoveOccupant = () => {
    let canRemove = true;
    //Si une prestation non obligatoire est trouvé, alors on ne peux pas supprimer l'occupant
    for (let i = 0; i < (occupant?.lineItems?.length ?? -1); i++) {
      const lineitem = occupant?.lineItems?.[i];
      if (lineitem && !lineitem?.product?.mandatory) {
        canRemove = false;
      }
    }
    return canRemove;
  };

  //remove an occupant from the stay
  const handleRemoveOccupant = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    occupantGuiid: number
  ) => {
    let canRemove = true;
    e.stopPropagation();

    //on check si on peux suppr l'occupant ne mode FCI
    if (isFromFCI && isFromFCI === true) {
      canRemove = canRemoveOccupant();
    }

    //Ne permettre la suppression des occupants secondaires que si et seulement si l'occupant n'a pas de prestation à la personne non obligatoires.
    if (canRemove) {
      setIsSubmittingRemoveOccupant(true);
      const toastDeleteOccupant = toast.loading(
        `${getLanguageVarValue(
          "LANG_neobooking_occupant_removing_in_progress"
        )}...`
      );
      removeOccupant(occupantGuiid, (err) => {
        if (err)
          toast.error(err.message, {
            id: toastDeleteOccupant,
          });
        else {
          toast.success(
            getLanguageVarValue(
              "LANG_neobooking_occupant_successfully_removed"
            ),
            {
              id: toastDeleteOccupant,
            }
          );
        }
        setIsSubmittingRemoveOccupant(false);
      });
    } else {
      if (isFromFCI && isFromFCI) {
        toast.error(
          getLanguageVarValue("LANG_neobooking_error_remove_occupant_fci")
        );
      }
    }
  };

  const validOccupant = isValidOccupant(
    occupant?.firstName ?? "",
    occupant?.lastName ?? "",
    occupant?.birthDate
  );

  /**
   * This function updates the birthdate of an occupant and calls an API to update the occupant's
   * information if the birthdate has changed.
   * @param {Date | null} e - The `e` parameter is of type `Date | null`, which means it can either be a
   * `Date` object or `null`. It represents the new birth date value that the user has selected.
   * @param {number} occupantGuiid - `occupantGuiid` is a unique identifier for the occupant whose
   * information is being updated. It is used to identify the specific occupant when making API calls to
   * update their information.
   * @returns The function `handleChangeDate` returns nothing (`void`).
   */
  const handleChangeDate = (e: Date | null, occupantGuiid: number) => {
    if (e) {
      let isSameDate = false;
      if (occupant.birthDate) {
        let d =
          occupant.birthDate instanceof Date
            ? occupant.birthDate
            : occupant.birthDate
            ? new Date(occupant.birthDate)
            : null;

        //Appel updateOccupant que si la date a changé
        if (isDate(d)) {
          isSameDate = compareDates(d, e);
        }
      }

      if (isSameDate) {
        return;
      }

      if (
        !isEmpty(occupantInfos.firstName?.trim()) &&
        !isEmpty(occupantInfos.lastName?.trim())
      ) {
        const toastUpdateOccupant = toast.loading(
          `${getLanguageVarValue(
            "LANG_neobooking_updating_occupant_data_in_progress"
          )}...`
        );
        updateOccupant(
          occupantGuiid,
          occupantInfos.firstName ?? "",
          occupantInfos.lastName ?? "",
          e instanceof Date ? e : new Date(e),
          (err) => {
            if (err)
              toast.error(err.message, {
                id: toastUpdateOccupant,
              });
            else {
              setOccupantInfos({ ...occupantInfos, birthDate: e });
              toast.success(
                getLanguageVarValue(
                  "LANG_neobooking_occupant_data_successfully_updated"
                ),
                {
                  id: toastUpdateOccupant,
                }
              );
              setOccupantSaved(occupantSaved + 1);
            }
          }
        );
      } else {
        toast.error(
          "Vous devez d'abord renseigner le nom et prénom avant de modifier la date de naissance"
        );
      }
    } else {
      toast.error(
        getLanguageVarValue("LANG_neobooking_occupant_invalid_birthdate")
      );
      focusElementById("txt_birthdate_" + occupantGuiid);
    }
  };
  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    mode: "fistname" | "lastname",
    occupantGuiid: number
  ) => {
    let edit = true;
    //On check que la chaine ne soit pas vide
    if (!isEmpty(e.target.value.trim())) {
      //On check si la nouvelle valeur n'est pas identique a l'ancienne, dans ce cas, on lance pas updateOccupant
      if (
        mode === "fistname" &&
        occupant.firstName?.trim() === e.target.value.trim()
      ) {
        edit = false;
      }

      if (
        mode === "lastname" &&
        occupant.lastName?.trim() === e.target.value.trim()
      ) {
        edit = false;
      }

      //Si le champs n'est pas vide et n'est pas identique a l'ancien, alors on update
      if (edit) {
        updateOccupant(
          occupantGuiid,
          mode === "fistname" ? e.target.value : occupantInfos.firstName!,
          mode === "lastname" ? e.target.value : occupantInfos.lastName!,
          occupantInfos.birthDate instanceof Date
            ? occupantInfos.birthDate
            : occupantInfos.birthDate
            ? new Date(occupantInfos.birthDate)
            : null,
          (err) => {
            if (err) toast.error(err.message);
            else {
              setOccupantSaved(occupantSaved + 1);
            }
          }
        );
      }
    }
  };
  return (
    <React.Fragment>
      <MyAccordion
        open={index === 0}
        title={(open) => (
          <div className="tw-flex tw-w-full tw-items-center">
            {showValidity ? (
              <div
                className={clsx(
                  "tw-h-[25px] tw-w-[25px] tw-rounded-full tw-text-white tw-flex tw-items-center tw-justify-center",
                  validOccupant ? "tw-bg-v_green" : "tw-bg-v_red-dark"
                )}
              >
                {validOccupant ? (
                  <CheckOutlined fontSize="small" />
                ) : (
                  <ChevronRightOutlined fontSize="small" />
                )}
              </div>
            ) : (
              <></>
            )}
            <div className="tw-flex tw-flex-col tw-p-2">
              <p className="tw-block tw-text-xl tw-font-bold tw-ptit-titre-principal-montserrat">
                {!isEmpty(occupantInfos.firstName) ||
                !isEmpty(occupantInfos.lastName) ? (
                  <>
                    {occupantInfos.lastName} {occupantInfos.firstName}
                  </>
                ) : (
                  <>
                    {getLanguageVarValue("LANG_neobooking_occupant")}{" "}
                    {index + 1}
                  </>
                )}
              </p>
              {showValidity ? (
                <p>
                  {validOccupant
                    ? occupant.isAdult
                      ? getLanguageVarValue("LANG_neobooking_adult")
                      : occupant?.isChildren1 || occupant?.isChildren2
                      ? getLanguageVarValue("LANG_neobooking_children")
                      : occupant?.isBaby
                      ? getLanguageVarValue("LANG_neobooking_baby")
                      : getLanguageVarValue("LANG_neobooking_occupant")
                    : getLanguageVarValue(
                        "LANG_neobooking_occupant_to_complete"
                      )}
                </p>
              ) : (
                <></>
              )}
            </div>
            {index !== 0 && canEditOccupant && (
              <Button
                type="button"
                variant="default"
                shape="link"
                onClick={(e) => handleRemoveOccupant(e, occupant.guiid!!)}
                isLoading={isSubmittingRemoveOccupant}
              >
                <FontAwesomeIcon
                  title={getLanguageVarValue(
                    "LANG_neobooking_occupant_remove_this_occupant"
                  )}
                  className="tw-text-v_blue-dark"
                  icon={faTrashAlt}
                />
              </Button>
            )}
          </div>
        )}
      >
        <div className="tw-grid tw-grid-cols-2 tw-gap-6">
          {showInfoOccupant && showInfoOccupant ? (
            <React.Fragment>
              <div className="tw-col-span-2">
                <InputText
                  id={`txt_firstname_${occupant.guiid}`}
                  required
                  disabled={
                    (disableEditFirstOccupant && index === 0) ||
                    (index === 0 && authMode === 2) ||
                    canEditOccupant === false
                  }
                  placeholder={getLanguageVarValue("LANG_neobooking_firstname")}
                  additionalClassname="tw-w-full occupant-input"
                  value={occupantInfos.firstName || ""}
                  onChange={(e) =>
                    setOccupantInfos({
                      ...occupantInfos,
                      firstName: e.target.value,
                    })
                  }
                  onBlur={(e) => handleBlur(e, "fistname", occupant.guiid!!)}
                />
              </div>
              <div className="tw-col-span-2">
                <InputText
                  id={`txt_lastname_${occupant.guiid}`}
                  required
                  disabled={
                    (disableEditFirstOccupant && index === 0) ||
                    (index === 0 && authMode === 2) ||
                    canEditOccupant === false
                  }
                  placeholder={getLanguageVarValue("LANG_neobooking_lastname")}
                  additionalClassname="tw-w-full occupant-input"
                  value={occupantInfos.lastName || ""}
                  onChange={(e) =>
                    setOccupantInfos({
                      ...occupantInfos,
                      lastName: e.target.value,
                    })
                  }
                  onBlur={(e) => handleBlur(e, "lastname", occupant.guiid!!)}
                />
              </div>
              <div className="tw-col-span-2">
                <SDPicker
                  disabled={
                    (disableEditFirstOccupant && index === 0) ||
                    (index === 0 && authMode === 2) ||
                    canEditOccupant === false
                  }
                  selectedSingleDate={
                    occupantInfos.birthDate
                      ? moment(occupantInfos.birthDate)
                      : null
                  }
                  setSelectedSingleDate={(e) =>
                    handleChangeDate(
                      e?.toDate() ?? new Date(),
                      occupant.guiid!!
                    )
                  }
                  occupantGuiid={occupant.guiid ?? 0}
                  isOutsideRange={(day) =>
                    isInclusivelyAfterDay(
                      day,
                      index === 0 ? moment().add(-18, "years") : moment()
                    )
                  }
                />
              </div>
            </React.Fragment>
          ) : (
            <></>
          )}
          {showProducts && showProducts && (
            <div className="tw-col-span-2">
              <OccupantProductList
                cartItemID={cartItemID}
                stayGuiid={stayGuiid}
                occupantIndex={index}
                occupantSaved={occupantSaved}
                occupantId={occupant.id ?? 0}
                occupantGuiid={occupant.guiid!!}
              />
            </div>
          )}
        </div>
      </MyAccordion>
    </React.Fragment>
  );
};
type OccupantProductListProps = {
  occupantIndex: number;
  occupantSaved: any;
  occupantGuiid: number;
  occupantId: number;
  stayGuiid: number;
  cartItemID: any;
};
const OccupantProductList: React.FC<OccupantProductListProps> = ({
  cartItemID,
  occupantSaved,
  occupantGuiid,
  occupantId,
  occupantIndex,
  stayGuiid,
}) => {
  /* Hooks */
  const client = useApolloClient();
  const { sessionId, getLanguageVarValue } = useSession();
  const { cart, addProductToOccupant, setCart } = useCart();
  const [occupantProducts, setOccupantProducts] = useState<ProductByCategory[]>(
    []
  );
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(true);
  const [loadingCopy, setLoadingCopy] = useState<boolean>(false);
  useEffect(() => {
    const abortController = new AbortController();
    if (sessionId) {
      fetchProducts(abortController);
    }
    return () => {
      abortController.abort();
    };
  }, [sessionId]);
  useEffect(() => {
    //Once an occupant is updated, fetch its product
    deleteCachedOccupantProducts(occupantGuiid.toString(), () => {
      fetchProducts();
    });
  }, [occupantSaved]);
  const fetchProducts = (abortController?: AbortController) => {
    setLoadingProducts(true);
    client
      .query<Query, QueryGetOccupantProductsArgs>({
        query: getOccupantProductsQuery(),
        variables: {
          session: { name: sessionId!! },
          input: {
            occupantGuiid: occupantGuiid,
          },
        },
        context: {
          fetchOptions: {
            signal: abortController?.signal ?? undefined,
          },
        },
        fetchPolicy: "no-cache",
      })
      .then((response) => {
        if (
          response.data &&
          response.data.getOccupantProducts?.occupantProducts
        ) {
          setOccupantProducts(
            response.data.getOccupantProducts
              ?.occupantProducts as ProductByCategory[]
          );
          setCachedProducts(
            "occupant",
            occupantGuiid,
            JSON.stringify(response.data.getOccupantProducts?.occupantProducts)
          );
        }
      })
      .catch((err: GraphQLError) => {
        console.log(err);
      })
      .finally(() => {
        setLoadingProducts(false);
      });
  };
  type ProductCopy = {
    product: Product;
    qte: number;
  };
  const handleCopy = () => {
    let items: Maybe<Lineitem[]> | undefined = undefined;
    cart?.reservations?.forEach((r) => {
      r?.stays?.forEach((s) => {
        s?.occupants?.forEach((o) => {
          if (o?.guiid === occupantGuiid) {
            items =
              (o?.lineItems?.filter(
                (x) =>
                  x?.product?.distributionType ===
                  ProductDistributionType.Occupant
              ) as Lineitem[]) ?? undefined;
          }
        });
      });
    });
    if (items !== undefined) {
      (({ ...cart } as Cart)?.reservations?.forEach((r) => {
        r?.stays?.forEach((s) => {
          s?.occupants?.forEach((o) => {
            if (o?.guiid !== occupantGuiid) {
              //on get les products des occupants, pas de celui courant...
              getCachedProducts("occupant", o?.guiid!!, (response) => {
                if (!response) {
                  client
                    .query<Query, QueryGetOccupantProductsArgs>({
                      query: getOccupantProductsQuery(),
                      variables: {
                        session: { name: sessionId!! },
                        input: {
                          occupantGuiid,
                        },
                      },
                      fetchPolicy: "no-cache",
                    })
                    .then(async (response) => {
                      let productToCopy: ProductCopy[] = [];
                      if (
                        response.data &&
                        response.data.getOccupantProducts?.occupantProducts
                      ) {
                        const products: ProductByCategory[] = response.data
                          .getOccupantProducts
                          ?.occupantProducts as ProductByCategory[];
                        let allOccupantProducts = products.map(
                          (x) => x.products
                        );
                        items?.forEach((i) => {
                          //i = selected lineitems for occupant
                          allOccupantProducts.forEach((arrayProduct) => {
                            arrayProduct?.forEach((x) => {
                              if (x?.code === i.product?.code) {
                                if (
                                  x?.code === i.product?.code &&
                                  x?.otherCode === i.product?.otherCode
                                ) {
                                  productToCopy = [
                                    ...productToCopy,
                                    { product: x!!, qte: i.quantity!! },
                                  ];
                                }
                                if (
                                  x?.subProducts &&
                                  Array.isArray(x.subProducts) &&
                                  Array(x.subProducts).length > 0
                                ) {
                                  x?.subProducts.forEach((sub) => {
                                    if (
                                      sub?.code === i.product?.code &&
                                      sub?.otherCode === i.product?.otherCode
                                    ) {
                                      productToCopy = [
                                        ...productToCopy,
                                        { product: sub!!, qte: i.quantity!! },
                                      ];
                                    }
                                  });
                                }
                              }
                            });
                          });
                        });
                        let errors: GraphQLError[] = [];
                        if (productToCopy.length > 0) {
                          setLoadingCopy(true);
                          for (let z = 0; z < productToCopy.length; z++) {
                            const currentProduct = productToCopy[z];
                            const c = await handleAsyncAddProductToOccupant(
                              currentProduct.product.code,
                              currentProduct.product.id,
                              o?.guiid!!,
                              currentProduct.qte
                            );
                            setCart(c!!);
                          }
                          setLoadingCopy(false);
                          toast.success(
                            getLanguageVarValue(
                              "LANG_neobooking_product_copy_to_occupant"
                            )
                          );
                        }
                      }
                    })
                    .catch((err: GraphQLError) => {
                      console.log(err);
                    });
                } else {
                  //on a une réponse en cache,
                  //on set la valeur qu'on a dedans
                  response?.json().then(async (data) => {
                    let productToCopy: ProductCopy[] = [];
                    const products = data as ProductByCategory[];
                    let allOccupantProducts = products.map((x) => x.products);
                    items?.forEach((i) => {
                      //i = selected lineitems for occupant
                      allOccupantProducts.forEach((arrayProduct) => {
                        arrayProduct?.forEach((x) => {
                          if (x?.code === i.product?.code) {
                            if (
                              x?.code === i.product?.code &&
                              x?.otherCode === i.product?.otherCode
                            ) {
                              productToCopy = [
                                ...productToCopy,
                                { product: x!!, qte: i.quantity!! },
                              ];
                            }
                            if (
                              x?.subProducts &&
                              Array.isArray(x.subProducts) &&
                              Array(x.subProducts).length > 0
                            ) {
                              x?.subProducts.forEach((sub) => {
                                if (
                                  sub?.code === i.product?.code &&
                                  sub?.otherCode === i.product?.otherCode
                                ) {
                                  productToCopy = [
                                    ...productToCopy,
                                    { product: sub!!, qte: i.quantity!! },
                                  ];
                                }
                              });
                            }
                          }
                        });
                      });
                    });
                    if (productToCopy.length > 0) {
                      setLoadingCopy(true);
                      for (let z = 0; z < productToCopy.length; z++) {
                        const currentProduct = productToCopy[z];
                        const c = await handleAsyncAddProductToOccupant(
                          currentProduct.product.code,
                          currentProduct.product.id,
                          o?.guiid!!,
                          currentProduct.qte
                        );
                        setCart(c!!);
                      }
                      setLoadingCopy(false);
                      toast.success(
                        getLanguageVarValue(
                          "LANG_neobooking_product_copy_to_occupant"
                        )
                      );
                    }
                  });
                }
              });
            }
          });
        });
      }));
    }
  };
  const handleAsyncAddProductToOccupant = async (
    productCode: string,
    productId: number,
    occupantGuiid: number,
    qte: number
  ) => {
    const data = await client.mutate<
      Mutation,
      MutationAddProductToOccupantArgs
    >({
      mutation: addProductToOccupantMutation(),
      variables: {
        session: { name: sessionId!! },
        input: [
          {
            productCode: productCode,
            productID: productId,
            occupantGuiid: occupantGuiid,
            quantity: qte,
          },
        ],
      },
      fetchPolicy: "no-cache",
    });
    return data.data?.addProductToOccupant?.cart;
  };

  const handleFilterProductByCriteria = (products: Maybe<Product>[]) => {
    let filteredProductsCriterias: ProductOrder[] = [];

    products?.forEach((p) => {
      let criteria = p?.cmsCriterias?.find((x) => x?.code === "PSORTW");

      let order = 99;

      try {
        order =
          criteria && criteria.code === "PSORTW" && criteria?.value
            ? parseInt(criteria.value)
            : 99;
      } catch (error) {
        order = 99;
      }

      filteredProductsCriterias = [
        ...filteredProductsCriterias,
        {
          order: order,
          product: p!!,
        },
      ];
    });

    const filtered = filteredProductsCriterias.sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      } else {
        return a.product.price?.amount - b.product.price?.amount;
      }
    });

    return filtered;
  };

  const canAddProduct = (product: Maybe<Product> | undefined) => {
    let canAdd = true;
    const isOTA =
      cart?.reservations?.[0]?.contractFolder?.cmsCriterias?.find(
        (x) => x?.code === "OTA"
      )?.value === "1";

    const isProductREM =
      product?.categoryCode === "SKI" &&
      (product?.code === "REM" || product?.otherCode === "RMN6");
    const isProductMATSKI =
      product?.categoryCode === "MAT" && product?.code?.startsWith("SKI");

    const isProductSPO =
      product?.categoryCode === "MAT" && product?.code?.startsWith("SPO");

    if (isOTA && isProductREM) {
      if (
        cart?.reservations?.[0]?.contractFolder?.cmsCriterias?.find(
          (x) => x?.code === "REM"
        )
      ) {
        const closingDay =
          cart?.reservations?.[0]?.contractFolder?.cmsCriterias?.find(
            (x) => x?.code === "REM"
          )?.value;
        const arrivalDate = moment(
          cart?.reservations?.[0]?.stays?.[0]?.startDate
        );

        //Calculer si on peux ajouter la prestation a J- closingDay par rapport a la date d'arrivée
        if (closingDay) {
          const closingDate = arrivalDate.subtract(
            parseInt(closingDay),
            "days"
          );
          const today = moment();
          if (today.isAfter(closingDate, "day")) {
            canAdd = false;
          }
        }
      }
    }
    if (isOTA && (isProductMATSKI || isProductSPO)) {
      if (
        cart?.reservations?.[0]?.contractFolder?.cmsCriterias?.find(
          (x) => x?.code === "MAT"
        )
      ) {
        const closingDay =
          cart?.reservations?.[0]?.contractFolder?.cmsCriterias?.find(
            (x) => x?.code === "MAT"
          )?.value;
        const arrivalDate = moment(
          cart?.reservations?.[0]?.stays?.[0]?.startDate
        );

        //Calculer si on peux ajouter la prestation a J- closingDay par rapport a la date d'arrivée
        if (closingDay) {
          const closingDate = arrivalDate.subtract(
            parseInt(closingDay),
            "days"
          );
          const today = moment();
          if (today.isAfter(closingDate, "day")) {
            canAdd = false;
          }
        }
      }
    }
    return canAdd;
  };

  return loadingProducts ? (
    <LoadingSpinner />
  ) : (
    <>
      {occupantProducts.filter((x) => x.categoryCode === "SKI").length > 0 &&
        occupantProducts
          .filter((x) => x.categoryCode === "SKI")?.[0]
          .products?.some((product) => canAddProduct(product)) && (
          <>
            <div className="tw-flex tw-items-center tw-my-8">
              <div className="tw-h-[1px] tw-w-[90%] tw-bg-black tw-hidden md:tw-block"></div>
              <div className="tw-flex-auto tw-w-full tw-min-w-[200px] tw-text-left md:tw-text-center tw-font-semibold tw-text-lg">
                {
                  occupantProducts.find((x) => x.categoryCode === "SKI")
                    ?.categoryLabel
                }
              </div>
              <div className="tw-h-[1px] tw-w-[90%] tw-bg-black"></div>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-4">
              {occupantProducts
                .find((x) => x.categoryCode === "SKI")
                ?.products?.map((p, pIndex) => {
                  return (
                    <OccupantProductItem
                      key={`panel_ski_product_${p?.id}_${pIndex}`}
                      showDelimiter={false}
                      stayGuiid={stayGuiid}
                      occupantGuiid={occupantGuiid}
                      occupantId={occupantId}
                      product={p!!}
                      canAddProduct={canAddProduct(p)}
                    />
                  );
                })}
            </div>
          </>
        )}
      {((occupantProducts.filter((x) => x.categoryCode === "MAT").length > 0 &&
        occupantProducts
          .filter((x) => x.categoryCode === "MAT")?.[0]
          .products?.some((pro) => canAddProduct(pro))) ||
        occupantProducts.filter((x) => x.categoryCode === "CAS").length >
          0) && (
        <>
          <div className="tw-flex tw-items-center tw-my-8">
            <div className="tw-h-[1px] tw-w-[90%] tw-bg-black tw-hidden md:tw-block"></div>
            <div className="tw-flex-auto tw-w-full tw-min-w-[220px] tw-text-left md:tw-text-center tw-font-semibold tw-text-lg">
              {getLanguageVarValue("LANG_neobooking_product_occupant_book_mat")}
            </div>
            <div className="tw-h-[1px] tw-w-[90%] tw-bg-black"></div>
          </div>
          <Tab.Group
            selectedIndex={selectedTabIndex}
            onChange={setSelectedTabIndex}
          >
            <Tab.List
              className={
                "tw-hidden md:tw-flex tw-justify-center tw-items-center tw-gap-2 tw-mb-4"
              }
            >
              {occupantProducts
                .filter(
                  (x) =>
                    x.categoryCode !== "SKI" &&
                    x.categoryCode !== "CIR" &&
                    x?.categoryCode !== "ACTIVITE"
                )
                .map((x, i) => {
                  return (
                    <Tab
                      as={Fragment}
                      key={`tab_category_${x.categoryCode}_${i}`}
                    >
                      {({ selected }: { selected: boolean }) => (
                        /* Use the `selected` state to conditionally style the selected tab. */

                        <button
                          className={classNames(
                            selected
                              ? `${
                                  mode === "vacanceole"
                                    ? "tw-bg-v_blue-dark tw-text-white"
                                    : "tw-bg-primary_corseole-500"
                                }`
                              : "tw-bg-v_blue-light tw-text-black",
                            "tw-px-3 tw-py-1 tw-shadow tw-rounded-full"
                          )}
                        >
                          {x.categoryLabel}
                        </button>
                      )}
                    </Tab>
                  );
                })}
            </Tab.List>
            <div className="tw-flex tw-justify-center tw-items-center md:tw-hidden tw-mb-4">
              <select
                className={`tw-border ${
                  mode === "vacanceole"
                    ? "tw-border-primary-500"
                    : "tw-border-primary_corseole-500"
                } tw-rounded tw-py-1 tw-px-2`}
                onChange={(e) => setSelectedTabIndex(parseInt(e.target.value))}
              >
                {occupantProducts
                  .filter((x) => x.categoryCode !== "SKI")
                  .map((x, i) => {
                    return (
                      <option
                        key={`opt_c_${x.categoryCode}_${i}_${x.categoryLabel}`}
                        value={i}
                      >
                        {x.categoryLabel}
                      </option>
                    );
                  })}
              </select>
            </div>
            <Tab.Panels>
              {occupantProducts
                .filter((x) => x.categoryCode !== "SKI")
                .map((x, productIndex) => {
                  return (
                    <Tab.Panel
                      key={`panel_category_${x.categoryCode}_${productIndex}`}
                      className={"tw-flex tw-flex-col tw-gap-4"}
                    >
                      {handleFilterProductByCriteria(
                        x?.products ? x.products : []
                      )?.map((product, pIndex) => {
                        return (
                          <OccupantProductItem
                            key={`panel_product_${product?.product.id}_${pIndex}`}
                            showDelimiter={false}
                            stayGuiid={stayGuiid}
                            occupantGuiid={occupantGuiid}
                            occupantId={occupantId}
                            product={product.product}
                            canAddProduct={canAddProduct(product.product)}
                          ></OccupantProductItem>
                        );
                      })}
                    </Tab.Panel>
                  );
                })}
            </Tab.Panels>
          </Tab.Group>
        </>
      )}
      {occupantProducts.filter((x) => x.categoryCode === "CIR").length > 0 && (
        <>
          <div className="tw-flex tw-items-center tw-my-8">
            <div className="tw-h-[1px] tw-w-[90%] tw-bg-black tw-hidden md:tw-block"></div>
            <div className="tw-flex-auto tw-w-full tw-min-w-[220px] tw-text-left md:tw-text-center tw-font-semibold tw-text-lg">
              {
                occupantProducts.find((x) => x.categoryCode === "CIR")
                  ?.categoryLabel
              }
            </div>
            <div className="tw-h-[1px] tw-w-[90%] tw-bg-black"></div>
          </div>
          <ProductList
            mode="occupant"
            cartItemID={cartItemID}
            stayGuiid={stayGuiid}
            occupantGuiid={occupantGuiid}
            categoryCodes={["CIR"]}
            excludeCategories={["SKI", "MAT", "CAS", "LOGEMENT"]}
            showCriteria={false}
          ></ProductList>
        </>
      )}
      {occupantProducts.filter((x) => x.categoryCode === "ACTIVITE").length >
        0 && (
        <>
          <div className="tw-flex tw-items-center tw-my-8">
            <div className="tw-h-[1px] tw-w-[90%] tw-bg-black tw-hidden md:tw-block"></div>
            <div className="tw-flex-auto tw-w-full tw-min-w-[200px] tw-text-left md:tw-text-center tw-font-semibold tw-text-lg">
              {
                occupantProducts.find((x) => x.categoryCode === "ACTIVITE")
                  ?.categoryLabel
              }
            </div>
            <div className="tw-h-[1px] tw-w-[90%] tw-bg-black"></div>
          </div>
          <div className="tw-flex tw-flex-col tw-gap-4">
            {occupantProducts
              .find((x) => x.categoryCode === "ACTIVITE")
              ?.products?.map((p, pIndex) => {
                return (
                  <OccupantProductItem
                    key={`panel_ACTIVITE_product_${p?.id}_${pIndex}`}
                    showDelimiter={false}
                    stayGuiid={stayGuiid}
                    occupantGuiid={occupantGuiid}
                    occupantId={occupantId}
                    product={p!!}
                    canAddProduct={canAddProduct(p)}
                  />
                );
              })}
          </div>
        </>
      )}
      {occupantProducts.filter((x) => x.categoryCode === "LOGEMENT").length >
        0 && (
        <>
          <div className="tw-flex tw-items-center tw-my-8">
            <div className="tw-h-[1px] tw-w-[90%] tw-bg-black tw-hidden md:tw-block"></div>
            <div className="tw-flex-auto tw-w-full tw-min-w-[220px] tw-text-left md:tw-text-center tw-font-semibold tw-text-lg">
              {
                occupantProducts.find((x) => x.categoryCode === "LOGEMENT")
                  ?.categoryLabel
              }
            </div>
            <div className="tw-h-[1px] tw-w-[90%] tw-bg-black"></div>
          </div>
          <ProductList
            mode="occupant"
            cartItemID={cartItemID}
            stayGuiid={stayGuiid}
            occupantGuiid={occupantGuiid}
            categoryCodes={["LOGEMENT"]}
            excludeCategories={["SKI", "MAT", "CAS", "CIR"]}
            showCriteria={false}
          ></ProductList>
        </>
      )}
      {(occupantProducts?.length ?? 0) > 0 && occupantIndex === 0 && (
        <div className="tw-flex tw-justify-center tw-mt-8">
          <Button shape="pills" isLoading={loadingCopy} onClick={handleCopy}>
            {getLanguageVarValue("LANG_neobooking_product_apply_to_occupant")}
          </Button>
        </div>
      )}
    </>
  );
};
type OccupantProductItemProps = {
  product: Product;
  occupantGuiid: number;
  occupantId: number;
  stayGuiid: number;
  showDelimiter: boolean;
  canAddProduct?: boolean;
};
const OccupantProductItem: React.FC<OccupantProductItemProps> = ({
  product,
  occupantGuiid,
  occupantId,
  stayGuiid,
  showDelimiter,
  canAddProduct = true,
}) => {
  /* Hooks */
  const { getLanguageVarValue, sessionId } = useSession();
  const { cart, addProductToOccupant, setCart, customerBooking } = useCart();
  const { mode: clientMode } = useConfig();
  const client = useApolloClient();
  const mode = clientMode ? clientMode : "vacanceole";

  const isFromFastCheckinOrCheckout = cart?.fromFastCheckinCheckout;

  useEffect(() => {
    cart?.reservations?.map((r) => {
      r?.stays?.map((s) => {
        s?.occupants?.map((o) => {
          if (o?.guiid === occupantGuiid) {
            o?.lineItems?.map((l) => {
              product?.subProducts?.map((sp) => {
                if (l?.product?.id === sp?.id) {
                  //si le linitem correspond a un subproduct, alors set le subproduct
                  setSelectedProductObject(sp!!);
                }
              });
            });
          }
        });
      });
    });
  }, []);

  const handleGetProductLabel = (): string => {
    let label = product.webLabel ? product.webLabel : product.label!!;
    if (
      (product.subProducts?.length ?? -1) <= 0 &&
      !isEmpty(product.otherLabel)
    ) {
      label += ` ${product?.otherLabel}`;
    }
    return label;
  };

  /* Selected product State */
  const [selectedProductObject, setSelectedProductObject] =
    useState<Product>(product);
  /* Add/remove State */
  const [addingProduct, setAddingProduct] = useState<boolean>(false);
  const [removingProduct, setRemovingProduct] = useState<boolean>(false);
  const handleQuantityChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const l = product?.subProducts?.find(
      (x) => x?.id === parseInt(e.target.value)
    );
    setSelectedProductObject(l ?? product);

    const added = isMainProductItem(occupantGuiid, getProductIds(product));
    //if subproduct already added, add product on select value change (avoid to remove and re add the product)
    if (added) {
      handleClickAddProduct(
        l?.id ?? product?.id!!,
        l?.code ?? product?.code!!,
        1
      );
    }
  };

  const handleClickAddProduct2 = (
    productId: number,
    productCode: string,
    qty: number
  ) => {
    setAddingProduct(true);

    addProductToOccupant(productCode, productId, occupantGuiid, 0, (err) => {
      if (!err) {
        addProductToOccupant(
          productCode,
          productId,
          occupantGuiid,
          qty,
          (e) => {
            if (e) toast.error(e.message);
            setAddingProduct(false);
          }
        );
      }
    });
  };

  const handleClickAddProduct = async (
    productId: number,
    productCode: string,
    qty: number
  ) => {
    setAddingProduct(true);
    try {
      const { data, errors } = await client.mutate<
        Mutation,
        MutationAddProductToOccupantArgs
      >({
        mutation: addProductToOccupantMutation(),
        variables: {
          session: { name: sessionId },
          input: [
            {
              occupantGuiid,
              productCode,
              productID: productId,
              quantity: 0,
            },
          ],
        },
        fetchPolicy: "no-cache",
      });

      if (data && data.addProductToOccupant && data.addProductToOccupant.cart) {
        //l'élément s'est bien supprimé, on peux l'ajouter
        const { data: dataAdd, errors: errorsAdd } = await client.mutate<
          Mutation,
          MutationAddProductToOccupantArgs
        >({
          mutation: addProductToOccupantMutation(),
          variables: {
            session: { name: sessionId },
            input: [
              {
                occupantGuiid,
                productCode,
                productID: productId,
                quantity: qty,
              },
            ],
          },
          fetchPolicy: "no-cache",
        });

        if (
          dataAdd &&
          dataAdd.addProductToOccupant &&
          dataAdd.addProductToOccupant.cart
        ) {
          setCart(dataAdd.addProductToOccupant.cart);
        }
      }
      setAddingProduct(false);
    } catch (error) {
      toast.error("Une erreur est survenue lors du choix du produit...");
      setAddingProduct(false);
    }
  };

  const canRemoveProduct2 = useMemo(() => {
    let canRemove = true;
    if (customerBooking !== null) {
      const newQuantity =
        cart?.reservations?.[0]?.stays
          ?.flatMap((stay) =>
            stay?.occupants
              ?.filter((x) => (occupantId ? x?.id === occupantId : x))
              ?.flatMap((occupant) => occupant?.lineItems)
          )
          ?.find(
            (lineItem) =>
              lineItem?.product?.id === selectedProductObject?.id &&
              lineItem?.product?.code === selectedProductObject?.code
          )?.quantity ?? 0;

      const oldQuantity =
        customerBooking?.stays
          ?.flatMap((stay) =>
            stay?.occupants
              ?.filter((x) => (occupantId ? x?.id === occupantId : x))
              ?.flatMap((occupant) => occupant?.lineItems)
          )
          ?.find(
            (lineItem) =>
              lineItem?.product?.id === selectedProductObject?.id &&
              lineItem?.product?.code === selectedProductObject?.code
          )?.quantity ?? 0;

      if (oldQuantity >= newQuantity) canRemove = false;
      if (oldQuantity === 0) canRemove = true;
      if (occupantGuiid && !occupantId) canRemove = true;
    }
    return canRemove;
  }, [customerBooking, occupantId, selectedProductObject]);

  const handleClickRemoveProduct = () => {
    if (canRemoveProduct2) {
      setRemovingProduct(true);
      addProductToOccupant(
        selectedProductObject.code,
        selectedProductObject.id,
        occupantGuiid,
        0,
        (err) => {
          if (err) toast.error(err.message);
          setRemovingProduct(false);
          setSelectedProductObject(product);
        }
      );
    } else {
      toast.error(
        getLanguageVarValue("LANG_neobooking_error_remove_product_fci")
      );
    }
  };
  const isMainProductItem = (occupantGuiid: number, productIds: number[]) => {
    let checked = false;
    cart?.reservations?.forEach((r) => {
      r?.stays?.forEach((s) => {
        s?.occupants?.forEach((o) => {
          if (o?.guiid === occupantGuiid) {
            o.lineItems?.forEach((l) => {
              if (productIds.includes(l?.product?.id!!)) {
                checked = true;
              }
            });
          }
        });
      });
    });
    return checked;
  };

  const getProductIds = (product: Product) => {
    const ids: number[] = [];
    if ((product.subProducts?.length ?? -1) > 0) {
      product.subProducts?.forEach((s) => {
        ids.push(s?.id!!);
      });
    } else {
      ids.push(product.id);
    }
    return ids;
  };

  const selectedSubProduct = (subProduct: Product) => {
    let selected = false;
    cart?.reservations?.forEach((r) => {
      r?.stays?.forEach((s) => {
        s?.occupants?.forEach((o) => {
          if (o?.guiid === occupantGuiid) {
            o.lineItems?.forEach((l) => {
              if (l?.product?.id === subProduct.id) {
                selected = true;
              }
            });
          }
        });
      });
    });
    return selected;
  };

  return (
    <>
      <div className="tw-flex tw-gap-x-2 tw-my-2 tw-bg-white tw-rounded-[26px] tw-border tw-border-[#707070]">
        <img
          className={clsx(
            "tw-w-[150px] md:tw-w-[150px] lg:tw-w-[200px] tw-min-h-[150px] md:tw-min-h-[200px] tw-rounded-[26px]",
            product.categoryCode === "MAT" || product.code === "MYTIG"
              ? "tw-object-contain"
              : "tw-object-cover"
          )}
          src={product.imagePath ?? ""}
          onError={replaceImgOnError}
        />
        <div className="tw-flex tw-flex-col tw-px-2 tw-py-4 tw-w-full">
          <p className="tw-font-bold tw-text-sm sm:tw-text-lg tw-font-montserrat tw-mb-2">
            {selectedProductObject?.webLabel
              ? selectedProductObject.webLabel
              : selectedProductObject.webLabel}
          </p>
          {selectedProductObject?.cmsCriterias?.find(
            (x) => x?.code === "WEBHTML"
          )?.value === "Oui" ? (
            <div
              dangerouslySetInnerHTML={{
                __html: selectedProductObject.description ?? "",
              }}
            ></div>
          ) : (
            <div className="tw-text-sm tw-text-black">
              <SeeMore
                popupTitle={
                  !isEmpty(product.webLabel)
                    ? product.webLabel!!
                    : product.label!!
                }
                defaultOpen={false}
                limit={160}
                content={selectedProductObject.description ?? ""}
                btnElement={(opened, toggle) => (
                  <button
                    className={`${
                      mode === "vacanceole"
                        ? "tw-text-red-500 hover:tw-text-red-400"
                        : "tw-text_corseole-secondary"
                    } tw-font-medium tw-font-montserrat focus:tw-outline-none`}
                    onClick={toggle}
                  >
                    {opened ? (
                      <img
                        className="tw-w-4 tw-rotate-180"
                        src={getAbsoluteImagePath(
                          "/assets/img/icons/arrow-bottom.svg"
                        )}
                      ></img>
                    ) : (
                      getLanguageVarValue("LANG_neobooking_see_more")
                    )}{" "}
                  </button>
                )}
              ></SeeMore>
            </div>
          )}
          <div className="tw-flex tw-flex-wrap tw-justify-between tw-h-full tw-items-end tw-mt-6">
            <div className="tw-flex tw-flex-col tw-font-spirit">
              {selectedProductObject.price?.amount !==
              selectedProductObject.priceWithoutDiscounts?.amount ? (
                <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-gap-x-2">
                  <span className="tw-text-xl tw-font-bold">
                    {selectedProductObject.price?.amount?.toFixed(2)}
                    {getCurrencySymbol(
                      selectedProductObject?.price?.currencyCode!!
                    )}
                  </span>
                  <span className="tw-line-through">
                    {selectedProductObject.priceWithoutDiscounts?.amount?.toFixed(
                      2
                    )}
                    {getCurrencySymbol(
                      selectedProductObject?.priceWithoutDiscounts
                        ?.currencyCode!!
                    )}
                  </span>
                </div>
              ) : (
                <div>
                  <span className="tw-text-xl tw-font-bold">
                    {selectedProductObject.price?.amount?.toFixed(2)}
                    {getCurrencySymbol(
                      selectedProductObject?.price?.currencyCode!!
                    )}
                  </span>
                </div>
              )}
            </div>
            <div className="tw-flex tw-flex-col tw-flex-none tw-gap-1">
              {(product?.subProducts?.length ?? -1) > 0 && (
                <div className="tw-max-w-[125px]">
                  <Select
                    onChange={handleQuantityChange}
                    disabled={
                      canRemoveProduct2 === false && canAddProduct === false
                    }
                    additionalClassname="tw-font-medium tw-font-montserrat"
                    options={() => (
                      <>
                        {product?.subProducts?.map((s, i) => {
                          return (
                            <option
                              className="tw-font-medium tw-font-montserrat"
                              selected={selectedSubProduct(s!!)}
                              key={`${s?.id}_i`}
                              value={s?.id}
                            >
                              {s?.otherLabel}
                            </option>
                          );
                        })}
                      </>
                    )}
                  />
                </div>
              )}
              <div className="tw-flex">
                {isMainProductItem(occupantGuiid, getProductIds(product)) ? (
                  <Button
                    disabled={removingProduct || canRemoveProduct2 === false}
                    isLoading={removingProduct}
                    variant="danger"
                    additionalClassname="tw-w-full tw-capitalize"
                    shape="pills"
                    onClick={handleClickRemoveProduct}
                  >
                    {getLanguageVarValue("LANG_neobooking_delete")}
                  </Button>
                ) : (
                  <Button
                    disabled={addingProduct || canAddProduct === false}
                    isLoading={addingProduct}
                    onClick={() =>
                      handleClickAddProduct(
                        selectedProductObject.id,
                        selectedProductObject.code,
                        1
                      )
                    }
                    additionalClassname="tw-w-full tw-capitalize"
                    shape="pills"
                    variant="primary"
                  >
                    {getLanguageVarValue("LANG_neobooking_add")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showDelimiter && showDelimiter && (
        <div className="tw-w-1/2 tw-mx-auto">
          <div className="tw-h-[1px] tw-bg-gray-600 tw-w-full"></div>
        </div>
      )}
    </>
  );
};
OccupantItem.propTypes = {
  //
};
export default OccupantItem;
